import { useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../../components/Layout';
import { Card, CardContent } from '../../components/ui/Card';
import { PageContainer } from '../../components/ui/FormContainer';
import {
  FileText,
  TrendingUp,
  DollarSign,
  Users,
  Building2,
  CreditCard,
  BarChart3,
  PieChart,
  Receipt,
  ChevronRight,
} from 'lucide-react';

const popularReports = [
  { id: 'vat-returns', name: 'VAT Returns', description: 'Manage and submit VAT returns', icon: Receipt, path: '/tenant/reports/vat-returns' },
  { id: 'chart-of-accounts', name: 'Chart of Accounts', description: 'View and manage nominal accounts', icon: FileText, path: '/tenant/reports/accounts' },
  { id: 'profit-loss', name: 'Profit & Loss', description: 'View P&L statement', icon: TrendingUp, path: '/tenant/reports/profit-loss' },
  { id: 'segmented-pl', name: 'Segmented P&L', description: 'P&L by period (monthly/quarterly/yearly)', icon: BarChart3, path: '/tenant/reports/segmented-pl' },
  { id: 'balance-sheet', name: 'Balance Sheet', description: 'View balance sheet', icon: DollarSign, path: '/tenant/reports/balance-sheet' },
  { id: 'trial-balance', name: 'Trial Balance', description: 'View trial balance report', icon: PieChart, path: '/tenant/reports/trial-balance' },
  { id: 'client-monthly-balance', name: 'Client Monthly Balance', description: 'Client balances by month', icon: Users, path: '/tenant/reports/client-monthly-balance' },
  { id: 'supplier-monthly-balance', name: 'Supplier Monthly Balance', description: 'Supplier balances by month', icon: Building2, path: '/tenant/reports/supplier-monthly-balance' },
  { id: 'journals', name: 'Journal Entries', description: 'Manage manual journal entries', icon: FileText, path: '/tenant/reports/journals' },
  { id: 'account-mapping', name: 'Account Mapping', description: 'Configure account mappings', icon: CreditCard, path: '/tenant/reports/account-mapping' },
];

const otherReports = [
  { id: 'debtor-creditor', name: 'Debtor / Creditor Report', description: 'List outstanding invoices and purchases', icon: Receipt, path: '/tenant/reports/debtor-creditor' },
  { id: 'client-ageing', name: 'Client Ageing Report', description: 'Ageing analysis for client invoices', icon: Users, path: '/tenant/reports/client-ageing' },
  { id: 'supplier-ageing', name: 'Supplier Ageing Report', description: 'Ageing analysis for supplier purchases', icon: Building2, path: '/tenant/reports/supplier-ageing' },
  { id: 'cash-based-pl', name: 'Cash-Based P&L', description: 'Profit & Loss based on cash received/paid', icon: DollarSign, path: '/tenant/reports/cash-based-pl' },
];

const reportTabs = [
  { id: 'popular', label: 'Popular' },
  { id: 'custom', label: 'Custom' },
  { id: 'projects', label: 'Projects' },
  { id: 'other', label: 'Other' },
];

function ReportLinkRow({ path, icon: Icon, title, description }) {
  return (
    <Link
      to={path}
      className="flex items-center gap-4 rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel)] p-4 transition-colors duration-180 hover:bg-[var(--ds-control-field-hover-bg)]"
    >
      <Icon className="h-5 w-5 shrink-0 text-[var(--ds-accent-primary)]" aria-hidden />
      <p className="flex-1 min-w-0 text-body leading-snug text-[var(--ds-text-primary)] break-words">
        <span className="font-medium">{title}</span>
        <span className="font-normal text-[var(--ds-text-muted)]"> - </span>
        <span className="font-normal text-meta text-[var(--ds-text-muted)]">{description}</span>
      </p>
      <ChevronRight className="h-5 w-5 shrink-0 text-[var(--ds-text-muted)]" aria-hidden />
    </Link>
  );
}

export default function Reports() {
  const [activeTab, setActiveTab] = useState('popular');

  return (
    <Layout>
      {/* Header */}
      <div className="bg-[var(--ds-surface-panel)] border-b border-[var(--ds-border-subtle)] shadow-[var(--ds-shadow-card)]">
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="md:flex md:items-center md:justify-between">
            <div className="flex-1 min-w-0">
              <h1 className="text-display text-[var(--ds-text-primary)] font-bold">Reports</h1>
              <p className="mt-1 text-meta text-[var(--ds-text-secondary)]">Financial reports and summaries</p>
            </div>
          </div>
        </div>
      </div>

      <PageContainer>
        <Card className="mb-6">
          <CardContent className="p-4 sm:p-6">
            <div
              className="flex flex-col sm:flex-row gap-1 sm:gap-1 p-1 rounded-[var(--ds-radius-md)] bg-[var(--ds-control-field-bg)] mb-6"
              role="tablist"
              aria-label="Report categories"
            >
              {reportTabs.map(({ id, label }) => (
                <button
                  key={id}
                  type="button"
                  role="tab"
                  aria-selected={activeTab === id}
                  onClick={() => setActiveTab(id)}
                  className={`flex-1 rounded-[var(--ds-radius-sm)] px-3 py-2.5 text-body font-medium transition-colors duration-180 ${
                    activeTab === id
                      ? 'bg-[var(--ds-surface-panel)] text-[var(--ds-text-primary)] shadow-[var(--ds-shadow-card)] border border-[var(--ds-border-subtle)]'
                      : 'text-[var(--ds-text-muted)] hover:text-[var(--ds-text-primary)]'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>

            {activeTab === 'popular' && (
              <div className="space-y-2">
                {popularReports.map((report) => (
                  <ReportLinkRow
                    key={report.id}
                    path={report.path}
                    icon={report.icon}
                    title={report.name}
                    description={report.description}
                  />
                ))}
              </div>
            )}

            {activeTab === 'custom' && (
              <div className="rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel)] p-8 text-center">
                <p className="text-meta text-[var(--ds-text-secondary)]">Custom reports coming soon</p>
              </div>
            )}

            {activeTab === 'projects' && (
              <div className="rounded-[var(--ds-radius-md)] border border-[var(--ds-border-subtle)] bg-[var(--ds-surface-panel)] p-8 text-center">
                <p className="text-meta text-[var(--ds-text-secondary)]">Project-based reports coming soon</p>
              </div>
            )}

            {activeTab === 'other' && (
              <div className="space-y-2">
                {otherReports.map((report) => (
                  <ReportLinkRow
                    key={report.id}
                    path={report.path}
                    icon={report.icon}
                    title={report.name}
                    description={report.description}
                  />
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </PageContainer>
    </Layout>
  );
}
